import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import host from "../../global";
import { toast } from "react-toastify";
import usePurchaseOptions from "../../hooks/usePurchaseOptions";
import GetMoreChoices, { getMoreOptions } from "../billing/GetMoreChoices";
import { creditForFeature, formatDate, formatCount, formatMoney } from "../billing/billingFormat";

const FEATURE_LABELS = {
  resume_screening_count: "CV screenings",
  bulk_upload: "bulk uploads",
  interview_count: "interviews",
  interview_count_v2: "interviews",
  // The scheduling allowance (+20% over the plan's interviews, for no-shows)
  interview_scheduled_count: "interviews",
  interview_scheduled_count_v2: "interviews",
  job_roles_count: "job roles",
  create_jd_v1_enabled: "Smart Mode Job Creation",
  create_jd_v1: "Smart Mode Job Creation",
  create_jd_v2_enabled: "Custom Mode Job Creation",
  create_jd_v2: "Custom Mode Job Creation",
  hr_count: "users",
  hr_creation_enabled: "Team members",
  mcq_enabled: "MCQ Tests",
  coding_enabled: "Coding Tests",
  behavioral_enabled: "Behavioral & Soft Skills",
  report_sharing: "Share Report"
};

/**
 * The pop-up for a blocked action: a credit ran out, a feature isn't on the
 * plan, or the plan has ended. What it offers comes from /purchase-options,
 * the same source as the Plans page and the heads-up, so they never disagree:
 *
 *   plan running, credit ran out -> move to a bigger plan, or add top-ups
 *                                   (pre-selected by the team's pace)
 *   Free, or plan ended          -> a plan, the next one up if pace beats it
 *   feature not on the plan      -> the first plan that has it, else sales
 *   recruiter                    -> "Let my admin know" (only admins pay)
 */
export default function LimitExceededModal({ isOpen, onClose, featureKey, errorCode, message }) {
  const overlayRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const credit = creditForFeature(featureKey);
  const { options, loading, reload } = usePurchaseOptions({ enabled: false, credit });
  const [lockedPlanIds, setLockedPlanIds] = useState(null);
  const [selected, setSelected] = useState(null);
  const [packs, setPacks] = useState(1);
  const [upgradeId, setUpgradeId] = useState(null);
  const [sending, setSending] = useState(false);

  const locked = errorCode === "FEATURE_NOT_AVAILABLE" || errorCode === "FEATURE_LOCKED";
  // A key with no label is still shown as words, never as the raw field name:
  // "job_roles_count_v2" -> "job roles".
  const featureLabel = featureKey
    ? (FEATURE_LABELS[featureKey]
      ?? featureKey.replace(/_v\d+$/, "").replace(/_(count|enabled)$/, "").replace(/_/g, " "))
    : null;
  // Out of the scheduling allowance rather than the interviews themselves:
  // some of the plan's interviews may be scheduled but not done yet.
  const schedulingLimit = /^interview_scheduled_count/.test(featureKey || "");

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Fresh each time it opens, so a purchase made in another tab shows up.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setUpgradeId(null);
    reload();
    // A locked feature: find which plans include it. Counts aren't the issue
    // there, so top-ups don't help.
    if (locked && featureKey) {
      axios.get(`${host}/get_plans`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` },
      }).then((res) => {
        setLockedPlanIds((res.data.plans || [])
          .filter((p) => p.is_purchasable && !(featureKey in (p.boolean_features || {})
            && p.boolean_features[featureKey] !== true))
          .map((p) => p.plan_id));
      }).catch(() => setLockedPlanIds([]));
    } else {
      setLockedPlanIds(null);
    }
  }, [isOpen, reload, locked, featureKey]);

  // The pre-selected choice, once the options arrive.
  useEffect(() => {
    if (!options || selected) return;
    const { preferred, packs: suggested } = getMoreOptions(options);
    setSelected(locked ? "upgrade" : preferred);
    setPacks(suggested);
  }, [options, selected, locked]);

  if (!isOpen) return null;

  const isAdmin = location.pathname.startsWith("/org-admin") && options?.can_buy !== false;
  const current = options?.current;
  const ended = errorCode === "SUBSCRIPTION_EXPIRED" || (current && !current.in_date);

  // For a locked feature, only plans that include it, and no top-ups.
  let view = options;
  if (options && locked && lockedPlanIds) {
    const plans = Object.fromEntries(Object.entries(options.plans || {}).map(([id, p]) => [
      id, lockedPlanIds.includes(id) ? p : { ...p, allowed: false },
    ]));
    const firstUnlocking = Object.values(plans).filter((p) => p.allowed).sort((a, b) => a.rank - b.rank)[0];
    view = {
      ...options,
      plans,
      topup: null,
      recommendation: firstUnlocking ? { action: "upgrade", plan_id: firstUnlocking.plan_id } : { action: "sales" },
    };
  }
  // The small "Or Corporate" link swaps which plan the upgrade row offers.
  const alternativeId = view?.recommendation?.alternative_plan_id;
  if (view && upgradeId && view.plans?.[upgradeId]?.allowed) {
    view = { ...view, recommendation: { ...view.recommendation, plan_id: upgradeId } };
  }
  const { upgrade, topup } = getMoreOptions(view);
  const alternative = alternativeId && view.plans?.[alternativeId]?.allowed
    ? (upgradeId === alternativeId ? view.plans[options.recommendation.plan_id] : view.plans[alternativeId])
    : null;
  const nothingToBuy = view && !upgrade && !topup;

  const total = credit === "cvs" ? options?.projections?.cvs?.total : options?.projections?.interviews?.total;
  const heading = ended
    ? `Your ${current?.plan_name || ""} plan has ended`.replace("  ", " ")
    : locked
    ? `${featureLabel ?? "This feature"} isn't on your plan`
    : schedulingLimit
    ? "You've scheduled as many interviews as your plan allows"
    : total
    ? `You've used all ${formatCount(total)} ${featureLabel || "credits"}`
    : `You've reached your ${featureLabel ?? "plan"} limit`;
  const subtext = message
    ? message
    : ended
    ? "Choose a plan to keep screening CVs and running interviews. Your jobs and candidates are kept."
    : locked
    ? "Move to a plan that includes it, or talk to us about what you need."
    : schedulingLimit && current?.in_date && !current?.is_trial
    ? `Interviews already scheduled still go ahead. To schedule more before ${formatDate(current.ends_on)}, add interviews or move to a bigger plan.`
    : schedulingLimit
    ? "Interviews already scheduled still go ahead. To schedule more, choose a plan."
    : current?.in_date && !current?.is_trial
    ? `Your plan runs until ${formatDate(current.ends_on)}. Add more now or move to a bigger plan.`
    : "Choose a plan to keep going. Your unused credits come with you.";

  const continueToPlans = () => {
    onClose();
    if (selected === "topup" && topup) navigate(`/org-admin/usage?topup=${packs}`);
    else if (upgrade) navigate(`/org-admin/usage?plan=${upgrade.plan_id}`);
    else navigate("/org-admin/usage");
  };

  const send = async (url, body, success) => {
    setSending(true);
    try {
      await axios.post(`${host}${url}`, body, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` },
      });
      toast.success(success);
      onClose();
    } catch {
      toast.error("Failed to send request. Please try again.");
    } finally {
      setSending(false);
    }
  };
  const requestDemo = () => send("/send-upgrade-request", {
    featureLabel: featureLabel ?? "Unknown Feature",
    isLocked: locked,
    requestType: "demo",
    recommendedPlan: upgrade?.display_name || "",
  }, "Demo requested. Our team will reach out shortly.");
  const contactSales = () => send("/send-upgrade-request", {
    featureLabel: featureLabel ?? "Unknown Feature",
    isLocked: locked,
    requestType: "upgrade",
  }, "Your request has been sent! Our team will reach out shortly.");
  const tellAdmin = () => send("/notify-admin-limit", {
    featureLabel: featureLabel ?? "credits",
    ended,
  }, "We've let your admin know.");

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
    >
      <div className="relative bg-white dark:bg-[#20202B] rounded-2xl shadow-xl w-full max-w-md p-6">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <div className="pr-8">
          <h2 className="text-[17px] font-semibold text-gray-900 dark:text-white leading-snug">
            {isAdmin ? heading : ended ? "Your team's plan has ended" : heading.replace("You've", "Your team has")}
          </h2>
          <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
            {isAdmin
              ? subtext
              : "Only your admin can add more or change the plan. We'll let them know what you need."}
          </p>
        </div>

        {isAdmin && (
          loading && !options ? (
            <div className="mt-5 h-[120px] rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse" />
          ) : nothingToBuy ? (
            <div className="mt-5 rounded-xl border border-[#EBEAED] dark:border-gray-700/80 p-4">
              <p className="text-[14px] font-semibold text-[#404040] dark:text-[#AEB9E1]">Enterprise</p>
              <p className="text-[12px] text-[#6B7280] dark:text-gray-400 mt-0.5">
                Custom volumes, unlimited users and a named success manager. Talk to sales.
              </p>
            </div>
          ) : view ? (
            <div className="mt-5">
              <GetMoreChoices
                options={view}
                selected={selected}
                onSelect={setSelected}
                packs={packs}
                onPacks={setPacks}
              />
              {view.recommendation?.pace > 0 && upgrade && selected === "upgrade" && !upgradeId && (
                <p className="mt-2 text-[11.5px] text-[#6B7280] dark:text-gray-400">
                  Suggested for your pace of about {formatCount(view.recommendation.pace)} interviews a quarter.
                </p>
              )}
            </div>
          ) : null
        )}

        <div className="mt-5 flex gap-2">
          {!isAdmin ? (
            <button
              onClick={tellAdmin}
              disabled={sending}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 disabled:opacity-60"
            >
              Let my admin know
            </button>
          ) : nothingToBuy ? (
            <button
              onClick={contactSales}
              disabled={sending}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:opacity-90 disabled:opacity-60"
            >
              Contact Sales
            </button>
          ) : (
            <button
              onClick={continueToPlans}
              disabled={!selected}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 disabled:opacity-60"
            >
              {selected === "topup" && topup
                ? `Add ${packs * topup.interviews_per_pack} interviews · ${formatMoney(topup.pack_price * packs, topup.currency)}`
                : upgrade
                ? `Continue with ${upgrade.display_name}`
                : "Continue"}
            </button>
          )}
          {isAdmin ? (
            <button
              onClick={requestDemo}
              disabled={sending}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold border border-[#2F64FF] text-[#2F64FF] hover:bg-[#2F64FF]/5 disabled:opacity-60"
            >
              Request a demo
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-full text-[13px] font-semibold border border-[#EBEAED] dark:border-gray-700 text-gray-600 dark:text-gray-300"
            >
              Close
            </button>
          )}
        </div>

        {isAdmin && (
          <div className="mt-3 flex flex-col items-center gap-1.5 text-[12px]">
            {alternative && (
              <button
                onClick={() => { setUpgradeId(alternative.plan_id); setSelected("upgrade"); }}
                className="text-[#2F64FF] hover:underline"
              >
                Or {alternative.display_name}: {formatCount(alternative.interviews)} interviews for {formatMoney(alternative.amount, alternative.currency)}
              </button>
            )}
            <button
              onClick={() => { onClose(); navigate("/org-admin/usage"); }}
              className="text-[#6B7280] dark:text-gray-400 hover:underline"
            >
              Compare all plans
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
